import { useCallback } from 'react';

import styled from '@emotion/styled';
import { IconButtonGhost } from '@kc-console/common-ui';
import { LayoutType, usePageLayoutState } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { RouterLink } from '@kc-console/shared-ui';
import { IcCloseLargeRegular, IcExternalRoundedRegular } from '@kep/kcds-icons';

import { useIsV2PanelSystem, usePanelTabHostAvailable } from './PanelTabHost';

import type { ReactNode } from 'react';

interface PanelActionsProps {
  /**
   * 닫기 버튼 비활성화 여부
   */
  disabledCloseButton?: boolean;
  /**
   * 링크 버튼 비활성화 여부
   */
  disabledLinkButton?: boolean;
  /**
   * 닫기 버튼 숨김 여부
   * - 탭 시스템에서는 탭 바에서 닫기를 처리하므로 true로 설정
   */
  hideCloseButton?: boolean;
  /**
   * 패널 닫기 핸들러 (필수)
   * - V2 패널 시스템에서는 반드시 외부에서 전달해야 함
   */
  onClose?: () => void;
  singleLinkPath?: string;
}

interface ActionButtonProps {
  'aria-label': string;
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export default function PanelActions(props: PanelActionsProps) {
  const {
    singleLinkPath = '',
    onClose,
    hideCloseButton = false,
    disabledCloseButton = false,
    disabledLinkButton = false,
  } = props;
  const { layoutType } = usePageLayoutState();
  const { t } = useTranslation();

  // V2 패널 시스템 사용 여부 (SplitDetailPanelProvider 존재)
  const isV2PanelSystem = useIsV2PanelSystem();
  // V2 탭 바 활성화 여부 (maxTabs > 1 && 탭 있음)
  const isV2TabBarActive = usePanelTabHostAvailable();

  const isDetailLayout = layoutType === LayoutType.detail;

  // singleLinkPath가 있으면 RouterLinkButton 표시
  // (selectedItems.length === 1 체크는 PanelHeader에서 singleLinkPath 계산 시 이미 처리됨)
  const hasRouterLink = Boolean(singleLinkPath);

  // 닫기 버튼 숨김 조건:
  // 1. hideCloseButton prop이 true
  // 2. V2 패널 시스템 + detail 레이아웃 (V2에서는 detail에서 닫기 버튼 불필요)
  // 3. V2 탭 바 활성화 (탭 바에서 닫기 처리)
  const shouldHideCloseButton =
    hideCloseButton || (isV2PanelSystem && isDetailLayout) || isV2TabBarActive;

  const handleClosePanel = useCallback(() => {
    if (!onClose) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PanelActions] onClose prop is required in V2 panel system');
      }
      return;
    }
    onClose();
  }, [onClose]);

  return (
    <ButtonContainer>
      {hasRouterLink && (
        <RouterLinkButton
          href={singleLinkPath}
          disabled={disabledLinkButton}
          ariaLabel={t('layout:panel.action.open_in_new_window')}
        />
      )}
      {!shouldHideCloseButton && (
        <CloseButton
          onClose={handleClosePanel}
          disabled={disabledCloseButton}
          ariaLabel={t('layout:panel.action.close')}
        />
      )}
    </ButtonContainer>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const RouterLinkButton = ({
  href,
  disabled,
  ariaLabel,
}: {
  ariaLabel: string;
  disabled: boolean;
  href: string;
}) => (
  <RouterLink href={href} disabled={disabled}>
    <ActionButton aria-label={ariaLabel} disabled={disabled}>
      <IcExternalRoundedRegular />
    </ActionButton>
  </RouterLink>
);

const CloseButton = ({
  onClose,
  disabled,
  ariaLabel,
}: {
  ariaLabel: string;
  disabled: boolean;
  onClose: () => void;
}) => (
  <ActionButton aria-label={ariaLabel} onClick={onClose} disabled={disabled}>
    <IcCloseLargeRegular />
  </ActionButton>
);

const ActionButton = ({
  'aria-label': ariaLabel,
  onClick,
  disabled,
  children,
}: ActionButtonProps) => (
  <IconButtonGhost size="sm" aria-label={ariaLabel} onClick={onClick} disabled={disabled}>
    {children}
  </IconButtonGhost>
);
